import { useState, useRef, useEffect } from 'react';
import type { BusinessType, BusinessReach, NationalOption, AudienceType, PhysicalPresence, UrlStatus, AgeRange, Gender } from '../types';

import { step1Schema, step2Schema, step3Schema, isValidStrictUrl } from '../../../utils/zod/onboardingSchemas';
import { productServiceNameSchema, emailRegex, urlRegex } from '../../../lib/validations/commonSchemas';
import { apiService } from "../../../services/api";
import { getPhoneParts } from '../../../lib/formatters/phone';
import {
  EMAIL_LOST_MESSAGE,
  isEmailRejection,
  isEmailTakenMessage,
  isUsableEmail,
  saveEmail,
  savedEmail,
  savedEmailVerified,
} from '../onboardingSession';

export function useOnboardingState() {

  const [currentStep, setCurrentStep] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = sessionStorage.getItem("currentStep");
      return saved ? Number(saved) : 1;
    }
    return 1;
  });

  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [userId, setUserId] = useState(() => {
    if (typeof window !== "undefined") {
      return sessionStorage.getItem("onboardingUserId") || "";
    }
    return "";
  });

  useEffect(() => {
    if (userId) {
      sessionStorage.setItem("onboardingUserId", userId);
    }
  }, [userId]);

  const [verifiedCompanyName, setVerifiedCompanyName] = useState(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const company = params.get("company_name");
      if (company) return company;
      return sessionStorage.getItem("verifiedCompanyName") || "";
    }
    return "";
  });

  useEffect(() => {
    if (verifiedCompanyName) {
      sessionStorage.setItem("verifiedCompanyName", verifiedCompanyName);
    } else {
      sessionStorage.removeItem("verifiedCompanyName");
    }
  }, [verifiedCompanyName]);

  const [savingData, setSavingData] = useState(false)

  //terms and conditions state
  const [termsAndConditionsId, setTermsAndConditionsId] = useState("1");

  // Recovery Data state
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [pendingRecoveryData, setPendingRecoveryData] = useState<any>(null);
  const [isDeletingTempData, setIsDeletingTempData] = useState(false);
  const [checkingForPreviousData, setCheckingForPreviousData] = useState(false);
  const lastCheckedEmail = useRef<string>("");
  const lastCheckedBrand = useRef<string>("");
  const [showEmailExistsModal, setShowEmailExistsModal] = useState(false);
  const [emailExistsMessage, setEmailExistsMessage] = useState('');
  
  const [showRegistrationRequiredModal, setShowRegistrationRequiredModal] = useState(false);
  const [registrationRedirectUrl, setRegistrationRedirectUrl] = useState('');

  // Step 1 state
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameEditing, setUsernameEditing] = useState(false);
  // Restored from this tab's session, like currentStep (see onboardingSession).
  // The OTP check is only carried over when resuming past step 1.
  const [email, setEmail] = useState(() => savedEmail());
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(() => currentStep > 1 && savedEmailVerified());
  const [emailOtpError, setEmailOtpError] = useState('');
  const [emailOtpCountdown, setEmailOtpCountdown] = useState(0);
  const [brandName, setBrandName] = useState(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const company = params.get("company_name");
      if (company) return company;
    }
    return "";
  });
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteUrlStatus, setWebsiteUrlStatus] = useState<UrlStatus>('idle');
  const [businessDescription, setBusinessDescription] = useState('');
  const [businessType, setBusinessType] = useState<BusinessType | null>(null);
  const [primaryCategory, setPrimaryCategory] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Agency state (Step 1)
  const [isManagedByAgency, setIsManagedByAgency] = useState(false);
  const [selectedAgencyFromList, setSelectedAgencyFromList] = useState<string | null>(null);
  const [showAgencyDropdown, setShowAgencyDropdown] = useState(false);
  const [agencyNotListed, setAgencyNotListed] = useState(false);
  const [agencyName, setAgencyName] = useState('');
  const [agencyId, setagencyId] = useState('')

  const [agencyContactName, setAgencyContactName] = useState('');
  const [agencyEmail, setAgencyEmail] = useState('');
  const [agencyPhone, setAgencyPhone] = useState('');
  const [agencyWebsite, setAgencyWebsite] = useState('');

  // Step 2 state
  const [businessReach, setBusinessReach] = useState<BusinessReach | null>(null);
  const [nationalOption, setNationalOption] = useState<NationalOption | null>(null);
  const [nationalCityInput, setNationalCityInput] = useState('');
  const [nationalCities, setNationalCities] = useState<string[]>([]);
  const [showNationalDropdown, setShowNationalDropdown] = useState(false);
  const [localLocations, setLocalLocations] = useState<string[]>([]);
  const [localLocationInput, setLocalLocationInput] = useState('');
  const [showLocalDropdown, setShowLocalDropdown] = useState(false);
  const [audienceType, setAudienceType] = useState<AudienceType | null>(null);
  const [personaDescription, setPersonaDescription] = useState('');
  const [targetAgeRange, setTargetAgeRange] = useState<AgeRange[]>([]);
  const [targetGender, setTargetGender] = useState<Gender[]>([]);
  const [interests, setInterests] = useState<string[]>([]);
  const [interestInput, setInterestInput] = useState('');
  const [professions, setProfessions] = useState<string[]>([]);
  const [professionInput, setProfessionInput] = useState('');
  const [physicalPresence, setPhysicalPresence] = useState<PhysicalPresence | null>(null);
  const [competitors, setCompetitors] = useState([
    { name: '', website: '', insights: '' },
    { name: '', website: '', insights: '' },
    { name: '', website: '', insights: '' },
    { name: '', website: '', insights: '' },
    { name: '', website: '', insights: '' },
  ]);
  const [visibleCompetitorCount, setVisibleCompetitorCount] = useState(2);

  // Step 3 state
  const [products, setProducts] = useState([{ name: '', url: '', priority: false }]);
  const [services, setServices] = useState([{ name: '', url: '', priority: false }]);
  const [differentiators, setDifferentiators] = useState(['']);
  const [socialPlatforms, setSocialPlatforms] = useState([
    { platform: 'Facebook', url: '' },
    { platform: 'Instagram', url: '' },
    { platform: 'LinkedIn', url: '' },
    { platform: 'Twitter/X', url: '' },
    { platform: 'YouTube', url: '' },
  ]);
  const [brandLogo, setBrandLogo] = useState<string | null>(null);
  const [primaryFont, setPrimaryFont] = useState('');
  const [secondaryFont, setSecondaryFont] = useState('');
  const [homePageUrl, setHomePageUrl] = useState('');
  const [productPageUrl, setProductPageUrl] = useState('');
  const [contactPageUrl, setContactPageUrl] = useState('');
  const [aboutPageUrl, setAboutPageUrl] = useState('');
  const [blogPageUrl, setBlogPageUrl] = useState('');
  const [servicePageUrl, setServicePageUrl] = useState('');
  const [customPageUrls, setCustomPageUrls] = useState([{ name: '', url: '' }]);
  const [primaryColor, setPrimaryColor] = useState('#54B8A6');
  const [secondaryColor, setSecondaryColor] = useState('#8D94BB');
  const [showPrimaryFontDropdown, setShowPrimaryFontDropdown] = useState(false);
  const [showSecondaryFontDropdown, setShowSecondaryFontDropdown] = useState(false);

  const urlValidationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const otpCountdownTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (otpCountdownTimer.current) clearInterval(otpCountdownTimer.current);
      if (urlValidationTimer.current) clearTimeout(urlValidationTimer.current);
    };
  }, []);



  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const setError = (field: string, message: string) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  const generateUsername = (name: string) => {
    if (!name.trim()) return '';
    const base = name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12);
    const suffix = Math.floor(Math.random() * 900) + 100;
    return `${base}${suffix}`;
  };

  const handleFullNameChange = (value: string) => {
    setFullName(value);
    if (!usernameEditing && value.trim()) {
      const generated = generateUsername(value);
      setUsername(generated);
      if (generated.length >= 3) {
        clearError('username');
      }
    }
  };

  const handleWebsiteUrlChange = (value: string) => {
    setWebsiteUrl(value);
    if (!value.trim()) {
      setWebsiteUrlStatus('idle');
      return;
    }
    setWebsiteUrlStatus('validating');
    if (urlValidationTimer.current) clearTimeout(urlValidationTimer.current);
    const timer = setTimeout(async () => {
      try {
        const url = new URL(value.startsWith('http') ? value : `https://${value}`);
        // const isValid = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)$/.test(value);
        const isValid = isValidStrictUrl(value);
        if (isValid && url.hostname.includes('.')) {
          const res = await apiService.validateUrl({ url: value });
          if (res.status === 'success' && res.reachable) {
            setWebsiteUrlStatus('valid');
          } else {
            setWebsiteUrlStatus('invalid');
          }
        } else {
          setWebsiteUrlStatus('invalid');
        }
      } catch {
        setWebsiteUrlStatus('invalid');
      }
    }, 700);
    urlValidationTimer.current = timer;
  };

  const handleSendEmailOtp = async (): Promise<boolean> => {
    // if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    if (!email.trim() || !emailRegex.test(email)) {
      setEmailOtpError('Please enter a valid email address');
      return false;
    }

    // Actively check if email exists before sending OTP
    if (email !== lastCheckedEmail.current) {
      setEmailOtpError('Verifying email...');
      try {
        const res = await apiService.getOnboardingData(verifiedCompanyName || brandName);
        lastCheckedEmail.current = email;
        if (res.success === false && res.message === "email id is alredy taken") {
          setEmailExistsMessage(res.message);
          setShowEmailExistsModal(true);
          setEmailOtpError('');
          return false;
        }
      } catch (error) {
        // Continue to OTP if API fails, fallback to standard validation
      }
    } else if (showEmailExistsModal) {
      return false;
    }

    try {
      await apiService.sendOtp({ type: 'email', value: email });
    } catch (error: any) {
      console.error('Failed to send OTP:', error);
      const detail = error?.response?.data?.detail || error?.response?.data?.message || 'Failed to send OTP. Please check the email and try again.';
      setEmailOtpError(detail);
      return false;
    }

    setEmailOtpError('');
    setEmailOtpSent(true);
    setEmailOtpCode('');
    setEmailOtpCountdown(60);
    if (otpCountdownTimer.current) clearInterval(otpCountdownTimer.current);
    otpCountdownTimer.current = setInterval(() => {
      setEmailOtpCountdown((prev) => {
        if (prev <= 1) {
          if (otpCountdownTimer.current) clearInterval(otpCountdownTimer.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return true;
  };

  const handleVerifyEmailOtp = (otp?: string) => {
    const code = otp || emailOtpCode;
    if (code.trim().length < 6) {
      setEmailOtpError('Enter a valid OTP code');
      return;
    }
    setEmailOtpError('');
    setEmailVerified(true);
    if (otpCountdownTimer.current) clearInterval(otpCountdownTimer.current);
    setEmailOtpCountdown(0);
  };

  const handleResendEmailOtp = () => {
    setEmailOtpCode('');
    setEmailOtpError('');
    handleSendEmailOtp();
  };

  const handleCloseEmailOtp = () => {
    if (otpCountdownTimer.current) clearInterval(otpCountdownTimer.current);
    setEmailOtpSent(false);
    setEmailOtpCode('');
    setEmailOtpError('');
    setEmailOtpCountdown(0);
  };

  const addProduct = () => {
    setProducts((prev) => [...prev, { name: '', url: '', priority: false }]);
  };

  const updateProduct = (index: number, field: 'name' | 'url', value: string) => {
    setProducts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    if (value.trim()) clearError('portfolio');
    
    if (field === 'name') {
      if (value.trim()) {
        const res = productServiceNameSchema.safeParse(value);
        if (!res.success) setError(`products_${index}_name`, res.error.errors[0].message);
        else clearError(`products_${index}_name`);
      } else {
        clearError(`products_${index}_name`);
      }
    }

    if (field === 'url') {
      // if (!value.trim() || /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\/?$/.test(value)) {
    if (!value.trim() || isValidStrictUrl(value)) {
        clearError(`products_${index}_url`);
      } else {
        setError(`products_${index}_url`, 'Please enter a valid URL');
      }
    }
  };

  const removeProduct = (index: number) => {
    setProducts((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const toggleProductPriority = (index: number) => {
    setProducts((prev) =>
      prev.map((p, i) => (i === index ? { ...p, priority: !p.priority } : p)),
    );
  };

  const addService = () => {
    setServices((prev) => [...prev, { name: '', url: '', priority: false }]);
  };

  const updateService = (index: number, field: 'name' | 'url', value: string) => {
    setServices((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    if (value.trim()) clearError('portfolio');
    
    if (field === 'name') {
      if (value.trim()) {
        const res = productServiceNameSchema.safeParse(value);
        if (!res.success) setError(`services_${index}_name`, res.error.errors[0].message);
        else clearError(`services_${index}_name`);
      } else {
        clearError(`services_${index}_name`);
      }
    }

    if (field === 'url') {
      // if (!value.trim() || /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\/?$/.test(value)) {
    if (!value.trim() || isValidStrictUrl(value)) {
        clearError(`services_${index}_url`);
      } else {
        setError(`services_${index}_url`, 'Please enter a valid URL');
      }
    }
  };

  const removeService = (index: number) => {
    setServices((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const toggleServicePriority = (index: number) => {
    setServices((prev) =>
      prev.map((s, i) => (i === index ? { ...s, priority: !s.priority } : s)),
    );
  };

  const addDifferentiator = () => {
    setDifferentiators((prev) => [...prev, '']);
  };

  const updateDifferentiator = (index: number, value: string) => {
    setDifferentiators((prev) => {
      const updated = [...prev];
      updated[index] = value.slice(0, 100);
      return updated;
    });
  };

  const updateSocialPlatform = (index: number, value: string) => {
    const truncatedValue = value.slice(0, 60);
    setSocialPlatforms((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], url: truncatedValue };
      return updated;
    });

    // if (!value.trim() || /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\/?$/.test(value)) {
    if (!truncatedValue.trim() || urlRegex.test(truncatedValue)) {
      clearError(`socialPlatforms_${index}_url`);
    } else {
      setError(`socialPlatforms_${index}_url`, 'Please enter a valid URL');
    }
  };

  const updateCompetitor = (index: number, field: 'name' | 'website' | 'insights', value: string) => {
    setCompetitors((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    
    if (value.trim()) clearError('competitors');

    if (field === 'name') {
      if (value.trim().length === 0) setError(`competitors_${index}_name`, 'Competitor brand name is required');
      else if (value.trim().length < 3) setError(`competitors_${index}_name`, 'Competitor brand name must be at least 3 characters');
      else clearError(`competitors_${index}_name`);
    }

    if (field === 'website') {
      if (value.trim().length === 0) {
        setError(`competitors_${index}_website`, 'Competitor website is required');
      // } else if (!/^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\/?$/.test(value)) {
      } else if (!isValidStrictUrl(value)) {
        setError(`competitors_${index}_website`, 'Please enter a valid root domain (e.g., example.com) without paths or deep links');
      } else {
        clearError(`competitors_${index}_website`);
      }
    }
  };

  const handleRemoveCompetitor = (index: number) => {
    setCompetitors((prev) => {
      const updated = [...prev];
      updated[index] = { name: '', website: '', insights: '' };
      return updated;
    });
  };

  const addLocalLocation = (location: string) => {
    if (location.trim() && !localLocations.includes(location.trim())) {
      setLocalLocations((prev) => [...prev, location.trim()]);
      clearError('localLocations');
    }
    setLocalLocationInput('');
    setShowLocalDropdown(false);
  };

  const removeLocalLocation = (index: number) => {
    setLocalLocations((prev) => prev.filter((_, i) => i !== index));
  };

  const addNationalCity = (city: string) => {
    if (city.trim() && !nationalCities.includes(city.trim())) {
      setNationalCities((prev) => [...prev, city.trim()]);
      clearError('nationalCities');
    }
    setNationalCityInput('');
    setShowNationalDropdown(false);
  };

  const removeNationalCity = (index: number) => {
    setNationalCities((prev) => prev.filter((_, i) => i !== index));
  };

  const addInterest = (interest: string) => {
    if (interest.trim()) {
      const parts = interest.split(',').map((p) => p.trim()).filter(Boolean);
      setInterests((prev) => {
        const next = [...prev];
        parts.forEach((part) => {
          if (!next.includes(part)) {
            next.push(part);
          }
        });
        return next;
      });
    }
    setInterestInput('');
  };

  const removeInterest = (index: number) => {
    setInterests((prev) => prev.filter((_, i) => i !== index));
  };

  const addProfession = (profession: string) => {
    if (profession.trim()) {
      const parts = profession.split(',').map((p) => p.trim()).filter(Boolean);
      setProfessions((prev) => {
        const next = [...prev];
        parts.forEach((part) => {
          if (!next.includes(part)) {
            next.push(part);
          }
        });
        return next;
      });
    }
    setProfessionInput('');
  };

  const removeProfession = (index: number) => {
    setProfessions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBusinessReachChange = (value: BusinessReach | null) => {
    setBusinessReach(value);
  };

  const handleLogoUpload = (logo: string) => {
    setBrandLogo(logo);
  };

  const addCustomPageUrl = () => {
    setCustomPageUrls((prev) => [...prev, { name: '', url: '' }]);
  };

  const updateCustomPageUrl = (index: number, field: 'name' | 'url', value: string) => {
    setCustomPageUrls((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    if (field === 'url') {
      // if (!value.trim() || /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\/?$/.test(value)) {
    if (!value.trim() || urlRegex.test(value)) {
        clearError(`customPageUrls_${index}_url`);
      } else {
        setError(`customPageUrls_${index}_url`, 'Please enter a valid URL');
      }
    }
  };

  const removeCustomPageUrl = (index: number) => {
    setCustomPageUrls((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };


  const hydrateStateWithData = (data: any) => {
    if (!data) return;

    console.log("Hydrating onboarding data:", data);

    if (data.email) {
      sessionStorage.setItem("email", data.email);
      setEmail(data.email);
      setEmailVerified(true);
    } else {
      sessionStorage.removeItem("email");
      setEmail("");
      setEmailVerified(false);
    }

    // =========================
    // STEP 1 STATE HYDRATION
    // =========================
    setFullName(data.fullName || "");
    setUsername(data.username || "");


    setBrandName(data.brandName || "");
    setWebsiteUrl(data.websiteUrl || "");
    setBusinessDescription(data.businessDescription || "");
    setBusinessType(data.businessType || null);
    setPrimaryCategory(data.primaryCategory || "");

    setIsManagedByAgency(data.isManagedByAgency || false);
    setagencyId(data.agencyId || "");
    setSelectedAgencyFromList(data.agencyId || null);
    setAgencyNotListed(data.agencyNotListed || false);
    setAgencyName(data.agencyName || "");
    setAgencyEmail(data.agencyEmail || "");
    setAgencyPhone(data.agencyPhone || "");
    setAgencyWebsite(data.agencyWebsite || "");
    setAgencyContactName(data.agencyContactName || "");

    // =========================
    // STEP 2 STATE HYDRATION
    // =========================
    setBusinessReach(data.businessReach || null);
    setLocalLocations(data.localLocations || []);
    setNationalOption(data.nationalOption || null);
    setNationalCities(data.nationalCities || []);
    setAudienceType(data.audienceType || null);

    setPersonaDescription(data.persona?.persona_description || data.personaDescription || "");
    const ageRange = data.persona?.target_age_range || data.targetAgeRange;
    setTargetAgeRange(Array.isArray(ageRange) ? ageRange : (ageRange ? [ageRange] : []));
    const gender = data.persona?.target_gender || data.targetGender;
    setTargetGender(Array.isArray(gender) ? gender : (gender ? [gender] : []));

    setInterests(data.persona?.interests || data.interests || []);
    setProfessions(data.persona?.professions || data.professions || []);

    setPhysicalPresence(data.physicalPresence || null);

    const recoveredCompetitors = (data.competitors || []).map((c: any) => ({
      name: c.competitor_name || c.name || "",
      website: c.competitor_website || c.website || "",
      insights: c.competitor_insights || c.insights || "",
    }));

    // Pad with empty objects up to 5
    const paddedCompetitors = [...recoveredCompetitors];
    while (paddedCompetitors.length < 5) {
      paddedCompetitors.push({ name: "", website: "", insights: "" });
    }
    setCompetitors(paddedCompetitors);

    // If we have more than 2 recovered competitors, update the visible count
    if (recoveredCompetitors.length > 2) {
      setVisibleCompetitorCount(recoveredCompetitors.length);
    } else {
      setVisibleCompetitorCount(2);
    }

    // =========================
    // STEP 3 STATE HYDRATION
    // =========================
    const recoveredProducts = (data.products || []).map((p: any) => ({
      name: p.product_name || p.name || "",
      url: p.product_url || p.url || "",
      priority: p.is_priority || p.priority || false,
    }));
    setProducts(recoveredProducts.length > 0 ? recoveredProducts : [{ name: "", url: "", priority: false }]);

    const recoveredServices = (data.services || []).map((s: any) => ({
      name: s.service_name || s.name || "",
      url: s.service_url || s.url || "",
      priority: s.is_priority || s.priority || false,
    }));
    setServices(recoveredServices.length > 0 ? recoveredServices : [{ name: "", url: "", priority: false }]);

    const recoveredDifferentiators = data.differentiators || [];
    setDifferentiators(recoveredDifferentiators.length > 0 ? recoveredDifferentiators : [""]);

    setHomePageUrl(data.page_urls?.home || "");
    setProductPageUrl(data.page_urls?.product || "");
    setServicePageUrl(data.page_urls?.service || "");
    setContactPageUrl(data.page_urls?.contact || "");
    setAboutPageUrl(data.page_urls?.about || "");
    setBlogPageUrl(data.page_urls?.blog || "");

    const recoveredCustomPages = (data.page_urls?.custom_pages || []).map((p: any) => ({
      name: p.custom_page_name || p.name || "",
      url: p.custom_page_url || p.url || "",
    }));
    setCustomPageUrls(recoveredCustomPages.length > 0 ? recoveredCustomPages : [{ name: "", url: "" }]);

    setBrandLogo(data.branding?.logo_url || "");
    setPrimaryFont(data.branding?.primary_font || "");
    setSecondaryFont(data.branding?.secondary_font || "");
    setPrimaryColor(data.branding?.primary_color || "#54B8A6");
    setSecondaryColor(data.branding?.secondary_color || "#8D94BB");

    setSocialPlatforms([
      { platform: "Facebook", url: data.social_urls?.facebook || "" },
      { platform: "Instagram", url: data.social_urls?.instagram || "" },
      { platform: "LinkedIn", url: data.social_urls?.linkedin || "" },
      { platform: "Twitter/X", url: data.social_urls?.twitter || "" },
      { platform: "YouTube", url: data.social_urls?.youtube || "" },
    ]);
  };

  const getOnboardingData = async (companyName?: string) => {
    const activeCompany = companyName || verifiedCompanyName || brandName;
    if (!activeCompany) return;
    if (activeCompany === lastCheckedBrand.current) return;

    lastCheckedBrand.current = activeCompany;
    setCheckingForPreviousData(true);

    try {
      const res = await apiService.getOnboardingData(activeCompany);

      // Registration check removed to allow onboarding of any email/brand
      /*
      if (res.registration_required) {
        setRegistrationRedirectUrl(res.redirect_url);
        setShowRegistrationRequiredModal(true);
        setCheckingForPreviousData(false);
        return;
      }
      */

      const responseData = res.data;
      if (res.success === false && res.message === "email id is alredy taken") {
        setEmailExistsMessage(res.message);
        setShowEmailExistsModal(true);
        setCheckingForPreviousData(false);
        return;
      }

      if (!responseData || !responseData.data) {
        setCheckingForPreviousData(false);
        return;
      }

      if (responseData.id) {
        setUserId(responseData.id);
      }

      setVerifiedCompanyName(activeCompany);

      const data = responseData.data;

      // Hydrate email if it is saved in the onboarding temp data
      if (data.email) {
        setEmail(data.email);
        setEmailVerified(true);
      } else {
        setEmail("");
        setEmailVerified(false);
        sessionStorage.removeItem("email");
      }

      // LEGACY IMPLEMENTATION
      // Retained for rollback.
      // Do not remove without approval.
      // if (currentStep > 1) {
      //   hydrateStateWithData(data);
      // } else {
      //   setPendingRecoveryData(data);
      //   setShowRecoveryModal(true);
      // }

      if (res.is_new_registration) {
        hydrateStateWithData(data);
      } else if (currentStep > 1) {
        hydrateStateWithData(data);
      } else {
        setPendingRecoveryData(data);
        setShowRecoveryModal(true);
      }

    } catch (error: any) {
      console.error(
        "Error fetching onboarding data:",
        error?.response?.data || error.message
      );
    } finally {
      setCheckingForPreviousData(false);
    }
  };

  const handleAcceptRecovery = () => {
    if (pendingRecoveryData) {
      hydrateStateWithData(pendingRecoveryData);
    }
    setShowRecoveryModal(false);
    setPendingRecoveryData(null);
  };

  const handleDeclineRecovery = async () => {
    setIsDeletingTempData(true);
    try {
      const activeCompany = verifiedCompanyName || brandName;
      if (activeCompany) {
        await apiService.deleteTempOnboardingData(activeCompany);
      } else if (email) {
        await apiService.deleteTempOnboardingData(email);
      }
    } catch (error) {
      console.error("Error deleting temp onboarding data:", error);
    } finally {
      // Clear non-core states to start fresh
      setEmail('');
      setEmailVerified(false);
      setEmailOtpSent(false);
      setEmailOtpCode('');
      sessionStorage.removeItem("email");

      setWebsiteUrl('');
      setWebsiteUrlStatus('idle');
      setBusinessDescription('');
      setBusinessType(null);
      setPrimaryCategory('');

      setIsManagedByAgency(false);
      setSelectedAgencyFromList(null);
      setAgencyNotListed(false);
      setAgencyName('');
      setAgencyEmail('');
      setAgencyPhone('');
      setAgencyWebsite('');
      setAgencyContactName('');
      setagencyId('');

      setBusinessReach(null);
      setLocalLocations([]);
      setNationalOption(null);
      setNationalCities([]);
      setAudienceType(null);
      setPersonaDescription('');
      setTargetAgeRange([]);
      setTargetGender([]);
      setInterests([]);
      setProfessions([]);
      setPhysicalPresence(null);

      setCompetitors([
        { name: "", website: "", insights: "" },
        { name: "", website: "", insights: "" },
        { name: "", website: "", insights: "" },
        { name: "", website: "", insights: "" },
        { name: "", website: "", insights: "" }
      ]);
      setVisibleCompetitorCount(2);

      setProducts([{ name: '', url: '', priority: false }]);
      setServices([{ name: '', url: '', priority: false }]);
      setDifferentiators(['']);
      setCustomPageUrls([{ name: '', url: '' }]);

      setHomePageUrl('');
      setProductPageUrl('');
      setServicePageUrl('');
      setContactPageUrl('');
      setAboutPageUrl('');
      setBlogPageUrl('');
      setBrandLogo('');
      setPrimaryFont('');
      setSecondaryFont('');
      setPrimaryColor('#54B8A6');
      setSecondaryColor('#8D94BB');

      setSocialPlatforms([
        { platform: "Facebook", url: "" },
        { platform: "Instagram", url: "" },
        { platform: "LinkedIn", url: "" },
        { platform: "Twitter/X", url: "" },
        { platform: "YouTube", url: "" }
      ]);

      setIsDeletingTempData(false);
      setShowRecoveryModal(false);
      setPendingRecoveryData(null);
    }
  };
  // Keep the email (and whether it was verified) for a reload; read back in the
  // email / emailVerified initial state above.
  useEffect(() => {
    saveEmail(email, emailVerified);
  }, [email, emailVerified]);


  useEffect(() => {
    sessionStorage.setItem("currentStep", String(currentStep));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  useEffect(() => {
    if (verifiedCompanyName) {
      const timer = setTimeout(() => {
        getOnboardingData(verifiedCompanyName);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      lastCheckedBrand.current = "";
    }
  }, [verifiedCompanyName]);

  const formatUrl = (url: string) => {
    return (!url.trim() || url.startsWith('http://') || url.startsWith('https://')) ? url : `https://${url}`;
  };

  const onboardingPayload = {
    email: email,
    company_name: verifiedCompanyName || brandName,
    completed_steps: currentStep,

    data: {
      termsAndConditionsId,
      fullName,
      username,
      email,
      brandName,

      websiteUrl,
      businessDescription,
      businessType,
      primaryCategory,

      isManagedByAgency,
      agencyNotListed,

      agencyId: agencyId ? agencyId : null,
      agencyName,
      agencyEmail,
      agencyContactName,
      agencyPhone,
      agencyWebsite,

      businessReach,

      localLocations,
      nationalOption,
      nationalCities,

      audienceType,

      persona: {
        target_age_range: targetAgeRange,
        target_gender: targetGender,
        interests: interests,
        professions: professions,
        persona_description: personaDescription,
      },

      physicalPresence,

      products: products
        .filter(p => p.name.trim() || p.url.trim())
        .map(p => ({
          product_name: p.name,
          product_url: formatUrl(p.url),
          is_priority: p.priority,
        })),

      services: services
        .filter(s => s.name.trim() || s.url.trim())
        .map(s => ({
          service_name: s.name,
          service_url: formatUrl(s.url),
          is_priority: s.priority,
        })),

      competitors: competitors
        .filter(c => c.name.trim() || c.website.trim() || c.insights.trim())
        .map(c => ({
          competitor_name: c.name,
          competitor_website: (!c.website.trim() || c.website.startsWith('http://') || c.website.startsWith('https://')) ? c.website : `https://${c.website}`,
          competitor_insights: c.insights,
        })),

      differentiators: differentiators.filter(d => d.trim()),

      page_urls: {
        home: formatUrl(homePageUrl),
        product: formatUrl(productPageUrl),
        service: formatUrl(servicePageUrl),
        contact: formatUrl(contactPageUrl),
        about: formatUrl(aboutPageUrl),
        blog: formatUrl(blogPageUrl),

        custom_pages: customPageUrls
          .filter(p => p.name.trim() || p.url.trim())
          .map(p => ({
            name: p.name,
            url: formatUrl(p.url),
          })),
      },

      branding: {
        logo_url: brandLogo || "",
        primary_font: primaryFont,
        secondary_font: secondaryFont,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
      },

      social_urls: {
        facebook: formatUrl(socialPlatforms.find(s => s.platform === "Facebook")?.url || ""),
        instagram: formatUrl(socialPlatforms.find(s => s.platform === "Instagram")?.url || ""),
        linkedin: formatUrl(socialPlatforms.find(s => s.platform === "LinkedIn")?.url || ""),
        twitter: formatUrl(socialPlatforms.find(s => s.platform === "Twitter/X")?.url || ""),
        youtube: formatUrl(socialPlatforms.find(s => s.platform === "YouTube")?.url || ""),
      },
    }
  };


  const handleStep1 = async () => {
    const step1Data = {
      fullName,
      username,
      email,
      emailVerified,
      brandName,
      websiteUrl,
      businessDescription,
      businessType,
      primaryCategory,
      isManagedByAgency,
      agencyNotListed,
      agencyEmail,
      agencyName,
      agencyId,
      agencyContactName,
      agencyPhone,
      agencyWebsite,
    };

    const result = step1Schema.safeParse(step1Data);

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors as Record<string, string[]>;
      
      if (isManagedByAgency && agencyNotListed) {
        if (!agencyName || agencyName.trim().length === 0) {
          fieldErrors.agencyName = fieldErrors.agencyName || ["Agency name is required"];
        } else if (agencyName.trim().length < 3) {
          fieldErrors.agencyName = fieldErrors.agencyName || ["Agency name must be at least 3 characters"];
        } else if (agencyName.trim().length > 60) {
          fieldErrors.agencyName = fieldErrors.agencyName || ["Agency name must be 60 characters or less"];
        }

        if (!agencyEmail || agencyEmail.trim().length === 0) {
          fieldErrors.agencyEmail = fieldErrors.agencyEmail || ["Agency email is required"];
        } else {
          const isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(agencyEmail) && 
                         !(/\.([a-zA-Z]{2,})\.\1$/.test(agencyEmail)) && 
                         !(/\.\./.test(agencyEmail));
          if (!isValid) fieldErrors.agencyEmail = fieldErrors.agencyEmail || ["Enter a valid agency email"];
        }

        if (!agencyContactName || agencyContactName.trim().length === 0) {
          fieldErrors.agencyContactName = fieldErrors.agencyContactName || ["Contact person name is required"];
        } else if (agencyContactName.trim().length < 3) {
          fieldErrors.agencyContactName = fieldErrors.agencyContactName || ["Contact person name must be at least 3 characters"];
        }

        if (!agencyPhone || agencyPhone.trim().length === 0) {
          fieldErrors.agencyPhone = fieldErrors.agencyPhone || ["Agency phone number is required"];
        } else {
          const { nationalNumber } = getPhoneParts(agencyPhone);
          if (nationalNumber.length !== 10) {
            fieldErrors.agencyPhone = fieldErrors.agencyPhone || ["Phone number must have exactly 10 digits"];
          }
        }
      }

      console.warn("Step 1 Validation Failed:", fieldErrors);

      setErrors({
        fullName: fieldErrors.fullName?.[0] || "",
        username: fieldErrors.username?.[0] || "",
        email: fieldErrors.email?.[0] || "",
        brandName: fieldErrors.brandName?.[0] || "",
        websiteUrl: fieldErrors.websiteUrl?.[0] || "",
        businessDescription: fieldErrors.businessDescription?.[0] || "",
        businessType: fieldErrors.businessType?.[0] || "",
        primaryCategory: fieldErrors.primaryCategory?.[0] || "",
        agencyName: fieldErrors.agencyName?.[0] || "",
        agencyId: fieldErrors.agencyId?.[0] || "",
        agencyEmail: fieldErrors.agencyEmail?.[0] || "",
        agencyContactName: fieldErrors.agencyContactName?.[0] || "",
        agencyPhone: fieldErrors.agencyPhone?.[0] || "",
        agencyWebsite: fieldErrors.agencyWebsite?.[0] || "",

        emailVerified:
          result.error.issues.find((i) =>
            i.path.includes("emailVerified"),
          )?.message || "",
      });
      return false;
    }

    console.log(step1Data)

    let createdAgencyId = agencyId;

    if (isManagedByAgency && agencyNotListed) {
      try {
        setSavingData(true)

        const res = await apiService.registerAgencyFromOnboarding({
          agency_name: agencyName,
          contact_person: agencyContactName,
          email: agencyEmail,
          phone: agencyPhone
        });

        console.log(res)

        createdAgencyId = res.agency_id;
        setagencyId(createdAgencyId);


      } catch (error) {
        setSavingData(false)
        console.log("error while register agency: ", error)

      }
    }

    const updatedStep1Data = {
      ...onboardingPayload,
      data: {
        user_id: userId,
        fullName,
        username,
        email,
        brandName,

        websiteUrl,
        businessDescription,
        businessType,
        primaryCategory,

        isManagedByAgency,
        agencyNotListed: isManagedByAgency ? agencyNotListed : false,

        agencyId: isManagedByAgency ? createdAgencyId : null,
        agencyName: isManagedByAgency ? agencyName : "",
        agencyEmail: isManagedByAgency ? agencyEmail : "",
        agencyContactName: isManagedByAgency ? agencyContactName : "",
        agencyPhone: isManagedByAgency ? agencyPhone : "",
        agencyWebsite: isManagedByAgency ? agencyWebsite : "",

        businessReach,

        localLocations,

        audienceType,

        targetAgeRange,
        targetGender,

        interests,
        professions,

        personaDescription,

        physicalPresence,

        products: products
          .filter(p => p.name.trim() || p.url.trim())
          .map(p => ({
            product_name: p.name,
            product_url: formatUrl(p.url),
            is_priority: p.priority,
          })),

        services: services
          .filter(s => s.name.trim() || s.url.trim())
          .map(s => ({
            service_name: s.name,
            service_url: formatUrl(s.url),
            is_priority: s.priority,
          })),

        differentiators: differentiators.filter(d => d.trim()),

        page_urls: {
          home: formatUrl(homePageUrl),
          product: formatUrl(productPageUrl),
          service: formatUrl(servicePageUrl),
          contact: formatUrl(contactPageUrl),
          about: formatUrl(aboutPageUrl),
          blog: formatUrl(blogPageUrl),
          custom_pages: customPageUrls
            .filter(p => p.name.trim() || p.url.trim())
            .map(p => ({
              name: p.name,
              url: formatUrl(p.url),
            })),
        },

        branding: {
          logo_url: brandLogo || "",
          primary_font: primaryFont,
          secondary_font: secondaryFont,
          primary_color: primaryColor,
          secondary_color: secondaryColor,
        },

        social_urls: {
          facebook: formatUrl(socialPlatforms.find(s => s.platform === "Facebook")?.url || ""),
          instagram: formatUrl(socialPlatforms.find(s => s.platform === "Instagram")?.url || ""),
          linkedin: formatUrl(socialPlatforms.find(s => s.platform === "LinkedIn")?.url || ""),
          twitter: formatUrl(socialPlatforms.find(s => s.platform === "Twitter/X")?.url || ""),
          youtube: formatUrl(socialPlatforms.find(s => s.platform === "YouTube")?.url || ""),
        },
      }
    };

    try {
      setSavingData(true)

      const res = await apiService.saveTempOnboardingData(updatedStep1Data);
      if (rejectedSave(res)) return false;

      console.log("Step 1 saved:", res);
      console.log("Step 1 saved:", res.data?.id);
      setUserId(res.data?.id || res.user_id)

      setErrors({});
      // setCompleteStep1(true)
      setSavingData(false)
      return true;

    } catch (error) {
      setSavingData(false)
      console.log("Server error from onboarding step 1", error)
      setErrors((prev) => ({
        ...prev,
        apiError: "Failed to save data. Please try again.",
      }));
      return false

    }
  };

  // The save endpoint answers some failures with 200 + success:false (e.g. "email
  // id is alredy taken"); those were treated as saved and the next step moved on.
  const rejectedSave = (res: any): boolean => {
    if (res?.success !== false) return false;
    setSavingData(false);
    const message = res.message || "Failed to save data. Please try again.";
    if (isEmailTakenMessage(message)) {
      setEmailExistsMessage(message);
      setShowEmailExistsModal(true);
    } else {
      setErrors((prev) => ({ ...prev, apiError: message }));
    }
    return true;
  };

  // Steps 2 and 3 save with the Step 1 email, and the backend rejects a save
  // without one (422). If it was lost (e.g. a reload that couldn't restore it),
  // go back to Step 1 to confirm it instead of failing every retry.
  const sendBackForEmail = () => {
    setSavingData(false);
    setEmailVerified(false);
    setCurrentStep(1);
    setErrors({ email: EMAIL_LOST_MESSAGE });
  };

  const handleStep2 = async () => {
    const step2ValidationData = {
      businessReach,
      nationalOption,
      nationalCities,
      localLocations,
      audienceType,
      personaDescription,
      targetAgeRange,
      targetGender,
      interests,
      professions,
      physicalPresence,
      competitors: competitors,
    };

    const result = step2Schema.safeParse(step2ValidationData);

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      console.warn("Step 2 Validation Failed:", fieldErrors);

      const newErrors: Record<string, string> = {
        businessReach: fieldErrors.businessReach?.[0] || "",
        nationalCities: fieldErrors.nationalCities?.[0] || "",
        localLocations: fieldErrors.localLocations?.[0] || "",
        audienceType: fieldErrors.audienceType?.[0] || "",
        physicalPresence: fieldErrors.physicalPresence?.[0] || "",
      };

      const competitorsGeneralError = result.error.issues.find(i => i.path.length === 1 && i.path[0] === "competitors");
      if (competitorsGeneralError) {
        newErrors.competitors = competitorsGeneralError.message;
      }

      result.error.issues.forEach(issue => {
        if (issue.path[0] === "competitors" && issue.path.length === 3) {
          const index = issue.path[1];
          const field = issue.path[2];
          newErrors[`competitors_${index}_${field}`] = issue.message;
        }
      });

      setErrors(newErrors);

      return false;
    }

    if (!isUsableEmail(email)) {
      sendBackForEmail();
      return false;
    }

    try {
      setSavingData(true)
      const res = await apiService.saveTempOnboardingData(onboardingPayload);
      if (rejectedSave(res)) return false;

      console.log(res);

      // setCompleteStep2(true);

      setErrors({});
      setSavingData(false)
      return true;
    } catch (error) {

      setSavingData(false)

      console.log(error);
      if (isEmailRejection(error)) {
        sendBackForEmail();
        return false;
      }
      setErrors((prev) => ({
        ...prev,
        apiError: "Failed to save step 2 data. Please try again.",
      }));
      return false;
    }
  };

  const handleStep3 = async () => {
    const step3ValidationData = {
      products,
      services,
      homePageUrl,
      productPageUrl,
      servicePageUrl,
      contactPageUrl,
      aboutPageUrl,
      blogPageUrl,
      customPageUrls,
      socialPlatforms,
    };

    const result = step3Schema.safeParse(step3ValidationData);

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      console.warn("Step 3 Validation Failed:", fieldErrors);

      const newErrors: Record<string, string> = {
        homePageUrl: fieldErrors.homePageUrl?.[0] || "",
        productPageUrl: fieldErrors.productPageUrl?.[0] || "",
        servicePageUrl: fieldErrors.servicePageUrl?.[0] || "",
        contactPageUrl: fieldErrors.contactPageUrl?.[0] || "",
        aboutPageUrl: fieldErrors.aboutPageUrl?.[0] || "",
        blogPageUrl: fieldErrors.blogPageUrl?.[0] || "",
      };

      const portfolioError = result.error.issues.find(i => i.path.length === 1 && i.path[0] === "portfolio");
      if (portfolioError) {
        newErrors.portfolio = portfolioError.message;
      }

      result.error.issues.forEach(issue => {
        if (["products", "services", "customPageUrls", "socialPlatforms"].includes(issue.path[0] as string) && issue.path.length >= 2) {
          const type = issue.path[0];
          const index = issue.path[1];
          const field = issue.path[2];
          newErrors[`${type}_${index}_${field}`] = issue.message;
        }
      });

      setErrors(newErrors);
      return false;
    } else {
      setErrors({});
    }

    const step3Data = {
      user_id: userId,

      products: products
        .filter((p) => p.name.trim() || p.url.trim())
        .map((p) => ({
          product_name: p.name,
          product_url: p.url,
          is_priority: p.priority,
        })),

      services: services
        .filter((s) => s.name.trim() || s.url.trim())
        .map((s) => ({
          service_name: s.name,
          service_url: s.url,
          is_priority: s.priority,
        })),

      differentiators: differentiators.filter((d) => d.trim()),

      page_urls: {
        home: homePageUrl,
        product: productPageUrl,
        service: servicePageUrl,
        contact: contactPageUrl,
        about: aboutPageUrl,
        blog: blogPageUrl,

        custom_pages: customPageUrls
          .filter((p) => p.name.trim() || p.url.trim())
          .map((p) => ({
            name: p.name,
            url: p.url,
          })),
      },

      branding: {
        logo_url: brandLogo || "",
        primary_font: primaryFont,
        secondary_font: secondaryFont,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
      },

      social_urls: {
        facebook:
          socialPlatforms.find((s) => s.platform === "Facebook")?.url || "",

        instagram:
          socialPlatforms.find((s) => s.platform === "Instagram")?.url || "",

        linkedin:
          socialPlatforms.find((s) => s.platform === "LinkedIn")?.url || "",

        twitter:
          socialPlatforms.find((s) => s.platform === "Twitter/X")?.url || "",

        youtube:
          socialPlatforms.find((s) => s.platform === "YouTube")?.url || "",
      },
    };

    console.log("STEP 3 DATA");
    console.log(step3Data);

    if (!isUsableEmail(email)) {
      sendBackForEmail();
      return false;
    }

    try {
      setSavingData(true)
      const res = await apiService.saveTempOnboardingData(onboardingPayload);
      if (rejectedSave(res)) return false;

      console.log("Step 3 saved:", res);
      setSavingData(false)
      // setCompleteStep3(true)

      return true;
    } catch (error) {
      setSavingData(false)

      console.log("Step 3 error:", error);
      if (isEmailRejection(error)) {
        sendBackForEmail();
        return false;
      }

      setErrors((prev) => ({
        ...prev,
        apiError: "Failed to save step 3 data. Please try again.",
      }));

      return false;
    }
  };

  const handleContinue = async () => {
    const scrollToFirstError = () => {
      setTimeout(() => {
        const errorElement = document.querySelector('p.text-red-500, #portfolio-error, .border-red-300, .border-red-400');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    };

    if (currentStep === 1) {
      console.log("enter hadleStep1...")
      const isValid = await handleStep1();
      if (!isValid) {
        scrollToFirstError();
        return;
      }
      await getOnboardingData(verifiedCompanyName || brandName)
    }

    if (currentStep === 2) {
      console.log("enter handleStep2")
      const isvalid = await handleStep2()
      if (!isvalid) {
        scrollToFirstError();
        return;
      }
      await getOnboardingData(verifiedCompanyName || brandName)
    }

    if (currentStep === 3) {
      console.log("enter handleStep3")
      const isvalid = await handleStep3()
      if (!isvalid) {
        scrollToFirstError();
        return;
      }
      await getOnboardingData(verifiedCompanyName || brandName)
    }

    // if(currentStep === 1){
    //   console.log(step1Data)
    // }

    // if(currentStep === 2) {
    //   console.log(step2Data)
    // }
    // if(currentStep === 3) {
    //   // console.log(step3Data)
    // }

    console.log("after all step position.....");

    if (currentStep < 3) {
      setCurrentStep((prev) => prev + 1);
    } else {
      if (!termsAccepted) {
        setShowTermsModal(true);
        return;
      }
      setShowSuccessScreen(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
    setShowSuccessScreen(true);
  };

  return {
    currentStep,
    userId,
    verifiedCompanyName,
    setVerifiedCompanyName,
    showSuccessScreen,
    setShowSuccessScreen,
    termsAccepted,
    showTermsModal,
    setTermsAccepted,
    setShowTermsModal,
    handleContinue,
    handleBack,
    handleAcceptTerms,
    //terms and conditions
    termsAndConditionsId,
    // Registration Required Modal
    showRegistrationRequiredModal,
    setShowRegistrationRequiredModal,
    registrationRedirectUrl,
    // Recovery
    showRecoveryModal,
    setShowRecoveryModal,
    pendingRecoveryData,
    checkingForPreviousData,
    isDeletingTempData,
    handleAcceptRecovery,
    handleDeclineRecovery,
    savingData,
    showEmailExistsModal,
    setShowEmailExistsModal,
    emailExistsMessage,
    // Step 1
    fullName,
    username,
    usernameEditing,
    email,
    emailOtpSent,
    emailOtpCode,
    emailVerified,
    emailOtpError,
    emailOtpCountdown,
    brandName,
    websiteUrl,
    websiteUrlStatus,
    businessDescription,
    businessType,
    primaryCategory,
    showCategoryDropdown,
    isManagedByAgency,
    selectedAgencyFromList,
    showAgencyDropdown,
    agencyNotListed,
    agencyName,
    agencyContactName,
    agencyEmail,
    agencyPhone,
    agencyWebsite,
    agencyId,
    setagencyId,
    setFullName,
    setUsername,
    setUsernameEditing,
    setEmail,
    setEmailOtpSent,
    setEmailOtpCode,
    setEmailVerified,
    setEmailOtpError,
    setEmailOtpCountdown,
    setBrandName,
    setWebsiteUrl,
    setWebsiteUrlStatus,
    setBusinessDescription,
    setBusinessType,
    setPrimaryCategory,
    setShowCategoryDropdown,
    setIsManagedByAgency,
    setSelectedAgencyFromList,
    setShowAgencyDropdown,
    setAgencyNotListed,
    setAgencyName,
    setAgencyContactName,
    setAgencyEmail,
    setAgencyPhone,
    setAgencyWebsite,
    errors,
    setErrors,
    // Step 2
    businessReach,
    nationalOption,
    nationalCityInput,
    nationalCities,
    showNationalDropdown,
    localLocations,
    localLocationInput,
    showLocalDropdown,
    audienceType,
    personaDescription,
    targetAgeRange,
    targetGender,
    interests,
    interestInput,
    professions,
    professionInput,
    physicalPresence,
    competitors,
    visibleCompetitorCount,
    setBusinessReach,
    setNationalOption,
    setNationalCityInput,
    setNationalCities,
    setShowNationalDropdown,
    setLocalLocations,
    setLocalLocationInput,
    setShowLocalDropdown,
    setAudienceType,
    setPersonaDescription,
    setTargetAgeRange,
    setTargetGender,
    setPhysicalPresence,
    setInterests,
    setInterestInput,
    setProfessions,
    setProfessionInput,
    setCompetitors,
    setVisibleCompetitorCount,
    // Step 3
    products,
    services,
    differentiators,
    socialPlatforms,
    brandLogo,
    primaryFont,
    secondaryFont,
    homePageUrl,
    productPageUrl,
    contactPageUrl,
    aboutPageUrl,
    blogPageUrl,
    servicePageUrl,
    customPageUrls,
    primaryColor,
    secondaryColor,
    showPrimaryFontDropdown,
    showSecondaryFontDropdown,
    setProducts,
    setServices,
    setDifferentiators,
    setSocialPlatforms,
    setBrandLogo,
    setPrimaryFont,
    setSecondaryFont,
    setHomePageUrl,
    setProductPageUrl,
    setContactPageUrl,
    setAboutPageUrl,
    setBlogPageUrl,
    setServicePageUrl,
    setCustomPageUrls,
    setPrimaryColor,
    setSecondaryColor,
    setShowPrimaryFontDropdown,
    setShowSecondaryFontDropdown,
    // Helpers
    clearError,
    setError,
    getOnboardingData,
    generateUsername,
    handleFullNameChange,
    handleWebsiteUrlChange,
    handleSendEmailOtp,
    handleVerifyEmailOtp,
    handleResendEmailOtp,
    handleCloseEmailOtp,
    addProduct,
    updateProduct,
    removeProduct,
    toggleProductPriority,
    addService,
    updateService,
    removeService,
    toggleServicePriority,
    addDifferentiator,
    updateDifferentiator,
    updateSocialPlatform,
    updateCompetitor,
    handleRemoveCompetitor,
    addLocalLocation,
    removeLocalLocation,
    addNationalCity,
    removeNationalCity,
    addInterest,
    removeInterest,
    addProfession,
    removeProfession,
    handleBusinessReachChange,
    handleLogoUpload,
    addCustomPageUrl,
    updateCustomPageUrl,
    removeCustomPageUrl,
  };
}